import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { LayoutDashboard, Package, ClipboardList, Users, Settings, ArrowLeft, Home, ShieldCheck, Tags, AlertTriangle } from "lucide-react";

const NAV = [
  { to: "/super-admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/super-admin/products", label: "Products", icon: Package },
  { to: "/super-admin/categories", label: "Categories", icon: Tags },
  { to: "/super-admin/orders", label: "Orders", icon: ClipboardList },
  { to: "/super-admin/users", label: "Users", icon: Users },
  { to: "/super-admin/homepage", label: "Homepage", icon: Home },
  { to: "/super-admin/settings", label: "Settings", icon: Settings },
  { to: "/super-admin/security", label: "Security", icon: ShieldCheck },
  // iter107 / Session J+ — payment-integrity console. Nav item renders
  // a live red badge with the unresolved orphan-payment count.
  { to: "/super-admin/payment-integrity", label: "Payment Integrity", icon: AlertTriangle, badgeKey: "orphan" },
];

export default function AdminLayout() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [orphanCount, setOrphanCount] = useState(0);

  useEffect(() => {
    // iter98 / Session E — accept BOTH legacy 'admin' and the new
    // 'super_admin' role.  Employee-admins are explicitly redirected
    // to their restricted /admin-orders view.
    if (!loading) {
      if (!user) {
        navigate("/login?redirect=/super-admin");
        return;
      }
      if (user.role === "employee_admin") {
        navigate("/admin-orders", { replace: true });
        return;
      }
      if (!["admin", "super_admin"].includes(user.role)) {
        navigate("/login?redirect=/super-admin");
      }
    }
  }, [user, loading, navigate]);

  // iter107 — poll the orphan-payment count so the red badge stays
  // fresh.  Cheap indexed query (~5ms).  60 s cadence + refresh on
  // window focus is enough for an alerting badge — every actual orphan
  // ALSO triggers a Main Email so a quiet tab is never a blocker.
  useEffect(() => {
    if (!user || !["admin", "super_admin"].includes(user.role)) return;
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const r = await api.get("/admin/payments/orphans/count");
        if (!cancelled) setOrphanCount(Number(r.data?.count || 0));
      } catch (_) {
        /* silent — nav must never crash on a monitoring miss */
      }
    };
    fetchCount();
    const t = setInterval(fetchCount, 60_000);
    const onFocus = () => fetchCount();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
    };
  }, [user]);

  if (!user || !["admin", "super_admin"].includes(user.role)) return null;

  return (
    <div className="container-x py-8 lg:py-12" data-testid="admin-layout">
      <div className="flex items-center gap-2 mb-8 text-xs uppercase tracking-wider text-brand-muted">
        <Link to="/" className="hover:text-brand-chili inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Back to store</Link>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3">
          <div className="bg-white border border-brand-line rounded-2xl p-6 sticky top-28">
            <h2 className="font-serif text-2xl text-brand-dark mb-1">Admin</h2>
            <p className="text-xs text-brand-muted mb-6">{user.email}</p>
            <nav className="space-y-1">
              {NAV.map((n) => {
                const badgeVal = n.badgeKey === "orphan" ? orphanCount : 0;
                return (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                        isActive ? "bg-brand-chili text-white" : "text-brand-dark hover:bg-brand-subtle"
                      }`
                    }
                    data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <n.icon className="w-4 h-4" strokeWidth={1.5} />
                    <span className="flex-1">{n.label}</span>
                    {badgeVal > 0 && (
                      <span
                        className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-600 text-white text-[11px] font-semibold leading-none"
                        data-testid="orphan-payment-badge"
                        aria-label={`${badgeVal} unresolved orphan payments`}
                      >
                        {badgeVal}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </nav>
          </div>
        </aside>
        <main className="lg:col-span-9">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
